import React, { useCallback, useContext, useMemo } from "react";
import * as yup from "yup";
import HeadingSection from "../UI/HeadingSection";
import { Form, Formik } from "formik";
import { Field, File, FormButtonCluster } from "../UI/forms";
import FieldArray from "../UI/forms/FieldArray";
import * as r from "../../requests/themeManagementRequests";
import AlertContext from "../../context/alert/AlertContext";
import { useNavigate, useParams } from "react-router-dom";
import ColorPicker from "../UI/forms/ColorPicker";
import Range from "../UI/forms/Range";
import Radio from "../UI/forms/Radio";
import Card from "../UI/Card";
import Button from "../UI/Button";
import { Heading, Body } from "../UI/Text";
import { PageWrapper } from "../UI/Grid";
import { useQuery } from "../Hooks";
import { seluxLight as defaultTheme } from "../../modules/theme";
import { Flex } from "reflexbox";
import { useTheme } from "styled-components";
import { getTheme } from "../../requests/themeManagementRequests";
import { getPlatforms } from "../../requests/platformManagementRequests";
import UserContext from "../../context/user/UserContext";
import Loading from "../UI/Loading";

const validationSchema = yup.object().shape({
  pid: yup.string().required("Platform ID is required!"),
  title: yup.string().nullable(),
  description: yup.string().nullable(),
  primary: yup.string().nullable(),
  secondary: yup.string().nullable(),
  headerColor: yup.string().nullable(),
  logo: yup.string().nullable(),
  logoDark: yup.string().nullable(),
  emailLogo: yup.string().nullable(),
  borderRadius: yup.string().nullable(),
  danger: yup.string().nullable(),
  success: yup.string().nullable(),
  warning: yup.string().nullable(),
  callToActionColor: yup.string().nullable(),
  tertiary: yup.string().nullable(),
  gray: yup.string().nullable(),
  grayDark: yup.string().nullable(),
  boxShadowColor: yup.string().nullable(),
  breakpoints: yup.array(yup.string().nullable()),
  textDark: yup.string().nullable(),
  backgroundColor: yup.string().nullable(),
  gridRowBackgroundColor: yup.string().nullable(),
  borderColor: yup.string().nullable(),
  headerTextColor: yup.string().nullable(),
  darkMode: yup.boolean().nullable(),
});

const ThemeForm = ({ refetchTheme }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

  const { showSuccessAlert, showFailureAlert } = useContext(AlertContext);
  const { pid, refetchPlatform } = useContext(UserContext);

  const platforms = useQuery({
    queryID: "platforms",
    request: getPlatforms,
  });
  const result = useQuery({
    queryID: `theme|${id}`,
    request: getTheme,
    queryVariables: { id },
    forcedFilters: {},
    skip: id === "create" || platforms?.isLoading || !platforms?.data,
  });

  const { data: currentTheme, isLoading, refetchQuery } = result || {};
  const { rows: _platforms = [] } = platforms?.data || {};
  const platformOptions = useMemo(() => {
    return _platforms.map(({ id, name }) => (
      <option key={id} value={id}>
        {name}
      </option>
    ));
  }, [_platforms]);

  const { breakpoints, name: title } = currentTheme || {};

  const initialValues = useMemo(
    () => ({
      ...(currentTheme || { defaultTheme }),
      title,
      breakpoints: JSON.parse(
        breakpoints || '["320px","360px","600px","800px","1000px","1200px"]'
      ),
    }),
    [currentTheme, breakpoints, title]
  );

  const onSubmit = useCallback(
    async (_values, { resetForm }) => {
      try {
        const { breakpoints, title: name, darkMode, ...values } = _values || {};
        const breakPointsString = JSON.stringify(breakpoints || []);

        const input = {
          ...values,
          breakpoints: breakPointsString,
          name,
          darkMode: darkMode ? "1" : "0",
        };

        if (id === "create") {
          const result = await r.createTheme({ input });

          navigate(`/Theme/${result?.id}`);

          showSuccessAlert(`Successfully created Theme: ${result.id}`);
          return;
        } else {
          await r.updateTheme({ id, input });

          refetchPlatform();
          refetchTheme();
          refetchQuery("themes");
          resetForm();
          showSuccessAlert(`Successfully updated Theme: ${id}`);
        }
      } catch (error) {
        console.error(error);
        showFailureAlert("Failed");
      }
    },
    [
      id,
      refetchQuery,
      refetchTheme,
      navigate,
      showSuccessAlert,
      showFailureAlert,
      refetchPlatform,
    ]
  );

  const onDelete = useCallback(() => {
    r.deleteTheme({ id: id });
    navigate(`/Themes/${pid}`);
  }, [id, navigate, pid]);

  const uploadFile = useCallback(
    async (file, fieldName) => {
      const {
        // name,
        //  size,
        // type,
        path,
      } = file;
      await onSubmit(
        { [fieldName]: `/uploads/theme/${path}` },
        { resetForm: () => {} }
      );
    },
    [onSubmit]
  );

  return !currentTheme || isLoading ? (
    <Loading />
  ) : (
    <PageWrapper>
      <Formik
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        initialValues={initialValues}
        enableReinitialize
      >
        {(formProps) => {
          const { values, setFieldValue } = formProps;

          return (
            <>
              <HeadingSection
                my="1rem"
                hideCreate
                backButtonLink={() => navigate(`/Themes/${pid}`)}
                title={`${id === "create" ? "Create" : "Edit"} Theme${
                  title ? ` - ${title}` : ""
                }`}
              />
              <Form style={{ width: "100%" }}>
                <Flex style={{ gap: "1rem" }}>
                  <Flex
                    style={{ gap: "1rem" }}
                    flexDirection="column"
                    width={[1, 1, 1, 1, 1, "calc(50% - 0.5rem)"]}
                  >
                    <Card flexGrow={0} justifyContent="flex-start" width={1}>
                      <Heading mt={0}>General Settings</Heading>

                      <Field
                        required
                        labelText="Assign to Platform"
                        name="pid"
                        as="select"
                      >
                        <option key="default" value="">
                          Select a Platform
                        </option>
                        {platformOptions}
                      </Field>

                      <Radio
                        name="darkMode"
                        labelText="Choose Theme Type"
                        options={[
                          {
                            value: false,
                            label: (
                              <Button
                                onClick={() => setFieldValue("darkMode", false)}
                                reverse
                                color={
                                  values.darkMode ? theme.gray : theme.secondary
                                }
                              >
                                Light Theme
                              </Button>
                            ),
                          },
                          {
                            value: true,
                            label: (
                              <Button
                                onClick={() => setFieldValue("darkMode", true)}
                                reverse
                                color={
                                  !values.darkMode
                                    ? theme.gray
                                    : theme.secondary
                                }
                              >
                                Dark Theme
                              </Button>
                            ),
                          },
                        ]}
                      />

                      <Field labelText="Name" name="title" />

                      <Field
                        labelText="Description"
                        as="textarea"
                        name="description"
                      />

                      <FieldArray
                        description="Enter breakpoints for different screen sizes in ascending order from smallest to largest screen width."
                        labelText="Device Break Points"
                        name="breakpoints"
                      />

                      <Range
                        labelText="Border Radius (px)"
                        description="This will add rounding to corners of UI elements. The higher this number is set, the more rounded the corners will look."
                        name="borderRadius"
                        step={1}
                        max={15}
                        min={0}
                      />
                    </Card>
                    <Card width={1}>
                      <Heading>Upload Theme Files</Heading>
                      <Body>
                        Files uploaded here will be automatically set without
                        the need to click "Save" at the bottom of the form.
                      </Body>

                      <Flex mb="-1rem" width={1} style={{ gap: "1rem" }}>
                        <Card
                          backgroundColor={values.headerColor}
                          my="1rem"
                          width={0.33}
                        >
                          <img src={values.logo} alt="logo" />
                        </Card>
                        <File
                          labelText="Header Logo"
                          multiple
                          onSubmit={(file) => uploadFile(file, "logo")}
                          name="logo"
                          path="/api/v1/themeManagement/uploadThemeFiles"
                        />
                      </Flex>

                      <Flex mb="-1rem" width={1} style={{ gap: "1rem" }}>
                        <Card my="1rem" width={0.33}>
                          <img src={values.emailLogo} alt="emailLogo" />
                        </Card>
                        <File
                          onSubmit={(file) => uploadFile(file, "emailLogo")}
                          name="emailLogo"
                          labelText="Email Header Logo"
                          path="/api/v1/themeManagement/uploadThemeFiles"
                        />
                      </Flex>
                      <Flex mb="-1rem" width={1} style={{ gap: "1rem" }}>
                        <Card
                          backgroundColor={values.secondary}
                          my="1rem"
                          width={0.33}
                        >
                          <img src={values.logoDark} alt="logoDark" />
                        </Card>
                        <File
                          onSubmit={(file) => uploadFile(file, "logoDark")}
                          name="logoDark"
                          labelText="Login Page Logo"
                          path="/api/v1/themeManagement/uploadThemeFiles"
                        />
                      </Flex>
                    </Card>
                  </Flex>

                  <Flex
                    style={{ gap: "1rem" }}
                    flexDirection="column"
                    width={[1, 1, 1, 1, 1, "calc(50% - 0.5rem)"]}
                  >
                    <Card width={1} flexWrap="wrap">
                      <Heading mt="0">Colors</Heading>
                      <Flex flexWrap="wrap">
                        <ColorPicker labelText="Primary" name="primary" />
                        <ColorPicker labelText="Secondary" name="secondary" />
                        <ColorPicker name="tertiary" labelText="Tertiary" />
                        <ColorPicker name="danger" labelText="Danger" />
                        <ColorPicker name="success" labelText="Success" />
                        <ColorPicker name="warning" labelText="Warning" />
                        <ColorPicker
                          name="callToActionColor"
                          labelText="Call To Action"
                        />

                        <ColorPicker name="gray" labelText="Gray" />

                        <ColorPicker name="grayDark" labelText="Dark Gray" />

                        <ColorPicker
                          name="backgroundColor"
                          labelText="Page Background"
                        />

                        <ColorPicker
                          name="gridRowBackgroundColor"
                          labelText="Table Row Offset"
                        />

                        <ColorPicker
                          name="borderColor"
                          labelText="Border"
                          description=""
                        />
                        <ColorPicker
                          name="boxShadowColor"
                          labelText="Box Shadow"
                        />

                        <ColorPicker name="textDark" labelText="Text" />
                        <ColorPicker labelText="Header" name="headerColor" />
                        <ColorPicker
                          labelText="Header Text"
                          name="headerTextColor"
                        />
                      </Flex>
                    </Card>
                  </Flex>
                </Flex>

                <FormButtonCluster
                  onDelete={onDelete}
                  onCancel={() => navigate(`/Themes/${currentTheme.pid}`)}
                  hideDelete={id === "create"}
                  recordAction={id === "create" ? "create" : "edit"}
                  deleteMessage={`Are you sure you want to delete ${values.name}?`}
                />
              </Form>
            </>
          );
        }}
      </Formik>
    </PageWrapper>
  );
};

export default ThemeForm;
